import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  StandardizedTable,
  formatNumber,
  TableColumn,
} from "@/components/ui/standardized-table";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { KpiNumber } from "@/components/ui/kpi-number";
import {
  CarregandoValor,
  EstadoDoValor,
  ValorIndisponivel,
} from "@/components/ui/estado-do-valor";
import {
  MIDIA_PAGA,
  type RequisitoDeFonte,
} from "@/lib/motivo-do-indisponivel";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignFiltersControl from "@/components/common/CampaignFiltersControl";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { FilterState, formatDateToISO } from "@/features/filters/filter.types";
import type { TableQueryDTO } from "@/types/table-query";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Users,
  Eye,
  Target,
  MonitorSpeaker,
  ArrowUpIcon,
  ArrowDownIcon,
  BarChart3,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
} from "recharts";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import {
  getMediaPerformanceGeneralPerformanceApiV1MediaPerformanceGeneralPerformanceGet,
  getMediaPerformanceInvestmentCpsApiV1MediaPerformanceInvestmentCpsGet,
  getMediaPerformanceInvestmentSessionsApiV1MediaPerformanceInvestmentSessionsGet,
  getMediaPerformanceInvestmentRevenueApiV1MediaPerformanceInvestmentRevenueGet,
  getMediaPerformanceSessionsConversionApiV1MediaPerformanceSessionsConversionGet,
  getMediaPerformanceSessionsRevenueApiV1MediaPerformanceSessionsRevenueGet,
  getMediaPerformanceSessionsAovApiV1MediaPerformanceSessionsAovGet,
  getMediaPerformanceSessionsRoasApiV1MediaPerformanceSessionsRoasGet,
  getMediaPerformanceInvestmentRoasApiV1MediaPerformanceInvestmentRoasGet,
  getMediaPerformanceRevenueConversionApiV1MediaPerformanceRevenueConversionGet,
  getMediaPerformanceAovConversionApiV1MediaPerformanceAovConversionGet,
  getMediaPerformanceSessionsBreakdownApiV1MediaPerformanceSessionsBreakdownGet,
} from "@/backend-sdk/client/sdk.gen";
import { Skeleton } from "@/components/ui/skeleton";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { formatSignedPercent } from "@/utils/numberFormat";
import useCampaignFiltersState from "@/hooks/useCampaignFiltersState";

// Interface para as métricas dos cards
interface MetricCard {
  title: string;
  rawValue: number | null;
  dataType: "CURRENCY" | "PERCENT" | "NUMBER";
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
  change?: number;
  changeType?: "up" | "down" | "neutral";
}

// Interface para dados do gráfico
interface ChartDataPoint {
  month: string;
  investimento?: number;
  investimentoGoogle?: number;
  investimentoMeta?: number;
  cps?: number;
  receita?: number;
  sessoes?: number;
  taxaConversao?: number;
  faturamento?: number;
  ticketMedio?: number;
  roas?: number;
}

interface ChartSeriesPoint {
  month?: string | null;
  month_label?: string | null;
  investment?: number | null;
  investment_google?: number | null;
  investment_meta?: number | null;
  cps?: number | null;
  revenue?: number | null;
  sessions?: number | null;
  conversion_rate?: number | null;
  average_order_value?: number | null;
  roas?: number | null;
}

interface MediaChartResponse {
  series?: ChartSeriesPoint[];
  summary?: Record<string, number | null | undefined>;
}

interface MediaCardsData {
  total_revenue?: number | null;
  total_revenue_change_percentage?: number;
  total_revenue_trend_indicator?: "up" | "down" | "neutral" | string;
  orders?: number | null;
  orders_change_percentage?: number;
  orders_trend_indicator?: "up" | "down" | "neutral" | string;
  average_order_value?: number | null;
  average_order_value_change_percentage?: number;
  average_order_value_trend_indicator?: "up" | "down" | "neutral" | string;
  conversion_rate?: number | null;
  conversion_rate_change_percentage?: number;
  conversion_rate_trend_indicator?: "up" | "down" | "neutral" | string;
  total_users?: number | null;
  total_users_change_percentage?: number;
  total_users_trend_indicator?: "up" | "down" | "neutral" | string;
  sessions?: number | null;
  sessions_change_percentage?: number;
  sessions_trend_indicator?: "up" | "down" | "neutral" | string;
  engaged_sessions?: number | null;
  engaged_sessions_change_percentage?: number;
  engaged_sessions_trend_indicator?: "up" | "down" | "neutral" | string;
  engagement_rate?: number | null;
  engagement_rate_change_percentage?: number;
  engagement_rate_trend_indicator?: "up" | "down" | "neutral" | string;
}

interface MediaTableRow {
  channel_group?: string | null;
  source_medium?: string | null;
  sessions?: number | null;
  total_revenue?: number | null;
  conversion_rate?: number | null;
  [key: string]: string | number | null | undefined;
}

interface MediaTableData {
  items: MediaTableRow[];
  pagination: { page: number; page_size: number; total: number };
}

interface ChartTooltipEntry {
  dataKey: string;
  value: number;
  color?: string;
}

/**
 * A tendência que a API publica, sem colapsar `neutral` em `down`: variação zero não é queda
 * (M-102).
 */
const direcaoDaTendencia = (
  tendencia: string | undefined,
): MetricCard["changeType"] =>
  tendencia === "up" || tendencia === "down" ? tendencia : "neutral";

const formatMonthLabel = (point: ChartSeriesPoint): string => {
  if (point.month_label) return point.month_label;
  if (!point.month) return "N/D";
  return new Date(point.month).toLocaleDateString("pt-BR", {
    month: "2-digit",
    year: "2-digit",
  });
};

const toNumericValue = (value: string | number | null | undefined): number =>
  Number(value ?? 0);

/**
 * O que cada número da tela precisa. Os oito cards e as sessões, receita, conversão e ticket
 * saem do GA4; o investimento é a soma das duas mídias; CPS e ROAS cruzam uma coisa com a outra.
 */
const FONTES: Readonly<
  Record<"ga4" | "investimento" | "ga4ComMidia", readonly RequisitoDeFonte[]>
> = {
  ga4: ["ga4"],
  investimento: [MIDIA_PAGA],
  ga4ComMidia: ["ga4", MIDIA_PAGA],
};

const MediaPerformancePage: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filterState);
  const { campaignFiltersNonce, handleCampaignFiltersSaved } =
    useCampaignFiltersState();
  const customerUuid = localStorage.getItem("active_customer_uuid") || null;
  const [loadingCards, setLoadingCards] = useState<boolean>(true);
  // As onze buscas saem num `Promise.all`: uma falha derruba todas, e o `catch` só zerava.
  const [erroDados, setErroDados] = useState<boolean>(false);
  const [cardsData, setCardsData] = useState<MediaCardsData | null>(null);
  const [loadingInvestmentCps, setLoadingInvestmentCps] =
    useState<boolean>(true);
  const [investmentCpsData, setInvestmentCpsData] =
    useState<MediaChartResponse | null>(null);
  const [loadingInvestmentSessions, setLoadingInvestmentSessions] =
    useState<boolean>(true);
  const [investmentSessionsData, setInvestmentSessionsData] =
    useState<MediaChartResponse | null>(null);
  const [loadingInvestmentRevenue, setLoadingInvestmentRevenue] =
    useState<boolean>(true);
  const [investmentRevenueData, setInvestmentRevenueData] =
    useState<MediaChartResponse | null>(null);
  const [loadingSessionsConversion, setLoadingSessionsConversion] =
    useState<boolean>(true);
  const [sessionsConversionData, setSessionsConversionData] =
    useState<MediaChartResponse | null>(null);
  const [loadingSessionsRevenue, setLoadingSessionsRevenue] =
    useState<boolean>(true);
  const [sessionsRevenueData, setSessionsRevenueData] =
    useState<MediaChartResponse | null>(null);
  const [loadingSessionsAov, setLoadingSessionsAov] = useState<boolean>(true);
  const [sessionsAovData, setSessionsAovData] =
    useState<MediaChartResponse | null>(null);
  const [loadingSessionsRoas, setLoadingSessionsRoas] = useState<boolean>(true);
  const [sessionsRoasData, setSessionsRoasData] =
    useState<MediaChartResponse | null>(null);
  const [loadingInvestmentRoas, setLoadingInvestmentRoas] =
    useState<boolean>(true);
  const [investmentRoasData, setInvestmentRoasData] =
    useState<MediaChartResponse | null>(null);
  const [loadingRevenueConversion, setLoadingRevenueConversion] =
    useState<boolean>(true);
  const [revenueConversionData, setRevenueConversionData] =
    useState<MediaChartResponse | null>(null);
  const [loadingAovConversion, setLoadingAovConversion] =
    useState<boolean>(true);
  const [aovConversionData, setAovConversionData] =
    useState<MediaChartResponse | null>(null);
  const [investCpsChartOptions, setInvestCpsChartOptions] = useState({
    investimento: true,
    cps: true,
    investimentoGoogle: false,
    investimentoMeta: false,
  });
  const [investRoasChartOptions, setInvestRoasChartOptions] = useState({
    investimento: true,
    roas: true,
    investimentoGoogle: false,
    investimentoMeta: false,
  });
  const [investRevenueChartOptions, setInvestRevenueChartOptions] = useState({
    investimento: true,
    receita: true,
    investimentoGoogle: false,
    investimentoMeta: false,
  });
  const [investSessionsChartOptions, setInvestSessionsChartOptions] = useState({
    investimento: true,
    sessoes: true,
    investimentoGoogle: false,
    investimentoMeta: false,
  });

  const handleFilterChange = (filter: FilterState) => {
    console.log("Filtro aplicado:", filter);
  };

  // Busca dos dados (KPI + Investimento x CPS) a partir do backend
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoadingCards(true);
        setLoadingInvestmentCps(true);
        setLoadingInvestmentSessions(true);
        setLoadingInvestmentRevenue(true);
        setLoadingSessionsConversion(true);
        setLoadingSessionsRevenue(true);
        setLoadingSessionsAov(true);
        setLoadingSessionsRoas(true);
        setLoadingInvestmentRoas(true);
        setLoadingRevenueConversion(true);
        setLoadingAovConversion(true);
        setErroDados(false);
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);
        const comparison =
          filterState.comparison?.type === "previousYear"
            ? "previous_year"
            : "previous_period";
        const customerUuid =
          localStorage.getItem("active_customer_uuid") || undefined;
        if (!customerUuid) {
          throw new Error("Customer UUID não encontrado");
        }
        // Para media-performance, não usar subaccount_id - dados estão agregados por customer
        const accountId = undefined;

        const [
          kpis,
          investCps,
          investSessions,
          investRevenue,
          sessionsConversion,
          sessionsRevenue,
          sessionsAov,
          sessionsRoas,
          investRoas,
          revenueConversion,
          aovConversion,
        ] = await Promise.all([
          getMediaPerformanceGeneralPerformanceApiV1MediaPerformanceGeneralPerformanceGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                account_id: accountId,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceInvestmentCpsApiV1MediaPerformanceInvestmentCpsGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceInvestmentSessionsApiV1MediaPerformanceInvestmentSessionsGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceInvestmentRevenueApiV1MediaPerformanceInvestmentRevenueGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceSessionsConversionApiV1MediaPerformanceSessionsConversionGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                account_id: accountId,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceSessionsRevenueApiV1MediaPerformanceSessionsRevenueGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                account_id: accountId,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceSessionsAovApiV1MediaPerformanceSessionsAovGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
            },
            throwOnError: true,
          }).then((r) => r.data),
          getMediaPerformanceSessionsRoasApiV1MediaPerformanceSessionsRoasGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
            },
            throwOnError: true,
          }).then((r) => r.data),
          getMediaPerformanceInvestmentRoasApiV1MediaPerformanceInvestmentRoasGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceRevenueConversionApiV1MediaPerformanceRevenueConversionGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                account_id: accountId,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
          getMediaPerformanceAovConversionApiV1MediaPerformanceAovConversionGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                account_id: accountId,
              },
              throwOnError: true,
            },
          ).then((r) => r.data),
        ]);

        setCardsData(kpis as MediaCardsData);
        setInvestmentCpsData(investCps as MediaChartResponse);
        setInvestmentSessionsData(investSessions as MediaChartResponse);
        setInvestmentRevenueData(investRevenue as MediaChartResponse);
        setSessionsConversionData(sessionsConversion as MediaChartResponse);
        setSessionsRevenueData(sessionsRevenue as MediaChartResponse);
        setSessionsAovData(sessionsAov as MediaChartResponse);
        setSessionsRoasData(sessionsRoas as MediaChartResponse);
        setInvestmentRoasData(investRoas as MediaChartResponse);
        setRevenueConversionData(revenueConversion as MediaChartResponse);
        setAovConversionData(aovConversion as MediaChartResponse);
      } catch (e) {
        setErroDados(true);
        setCardsData(null);
        setInvestmentCpsData(null);
        setInvestmentSessionsData(null);
        setInvestmentRevenueData(null);
        setSessionsConversionData(null);
        setSessionsRevenueData(null);
        setSessionsAovData(null);
        setSessionsRoasData(null);
        setInvestmentRoasData(null);
        setRevenueConversionData(null);
        setAovConversionData(null);
      } finally {
        setLoadingCards(false);
        setLoadingInvestmentCps(false);
        setLoadingInvestmentSessions(false);
        setLoadingInvestmentRevenue(false);
        setLoadingSessionsConversion(false);
        setLoadingSessionsRevenue(false);
        setLoadingSessionsAov(false);
        setLoadingSessionsRoas(false);
        setLoadingInvestmentRoas(false);
        setLoadingRevenueConversion(false);
        setLoadingAovConversion(false);
      }
    };
    fetchAll();
  }, [filterState, campaignFiltersNonce]);

  const metricsCards: MetricCard[] = useMemo(() => {
    // Sem resposta, os cards saem assim mesmo: o indicador ou o N/D com o motivo ocupa o número.
    const c: MediaCardsData = cardsData ?? {};
    return [
      {
        title: "Receita Total",
        rawValue:
          c.total_revenue === null || c.total_revenue === undefined
            ? null
            : Number(c.total_revenue),
        dataType: "CURRENCY",
        icon: DollarSign,
        bgColor: "bg-success-soft",
        change: c.total_revenue_change_percentage,
        changeType: direcaoDaTendencia(c.total_revenue_trend_indicator),
      },
      {
        title: "Pedidos",
        rawValue:
          c.orders === null || c.orders === undefined ? null : Number(c.orders),
        dataType: "NUMBER",
        icon: ShoppingCart,
        bgColor: "bg-primary/10",
        change: c.orders_change_percentage,
        changeType: direcaoDaTendencia(c.orders_trend_indicator),
      },
      {
        title: "Ticket Médio",
        rawValue:
          c.average_order_value === null || c.average_order_value === undefined
            ? null
            : Number(c.average_order_value),
        dataType: "CURRENCY",
        icon: TrendingUp,
        bgColor: "bg-accent/10",
        change: c.average_order_value_change_percentage,
        changeType: direcaoDaTendencia(c.average_order_value_trend_indicator),
      },
      {
        title: "Taxa Conversão",
        rawValue:
          c.conversion_rate === null || c.conversion_rate === undefined
            ? null
            : Number(c.conversion_rate),
        dataType: "PERCENT",
        icon: Target,
        bgColor: "bg-primary/10",
        change: c.conversion_rate_change_percentage,
        changeType: direcaoDaTendencia(c.conversion_rate_trend_indicator),
      },
      {
        title: "Usuários",
        rawValue:
          c.total_users === null || c.total_users === undefined
            ? null
            : Number(c.total_users),
        dataType: "NUMBER",
        icon: Users,
        bgColor: "bg-primary/10",
        change: c.total_users_change_percentage,
        changeType: direcaoDaTendencia(c.total_users_trend_indicator),
      },
      {
        title: "Sessões",
        rawValue:
          c.sessions === null || c.sessions === undefined
            ? null
            : Number(c.sessions),
        dataType: "NUMBER",
        icon: Eye,
        bgColor: "bg-primary/10",
        change: c.sessions_change_percentage,
        changeType: direcaoDaTendencia(c.sessions_trend_indicator),
      },
      {
        title: "Sessões Engajadas",
        rawValue:
          c.engaged_sessions === null || c.engaged_sessions === undefined
            ? null
            : Number(c.engaged_sessions),
        dataType: "NUMBER",
        icon: MonitorSpeaker,
        bgColor: "bg-primary/10",
        change: c.engaged_sessions_change_percentage,
        changeType: direcaoDaTendencia(c.engaged_sessions_trend_indicator),
      },
      {
        title: "Taxa Engajamento",
        rawValue:
          c.engagement_rate === null || c.engagement_rate === undefined
            ? null
            : Number(c.engagement_rate),
        dataType: "PERCENT",
        icon: Target,
        bgColor: "bg-primary/10",
        change: c.engagement_rate_change_percentage,
        changeType: direcaoDaTendencia(c.engagement_rate_trend_indicator),
      },
    ];
  }, [cardsData]);

  // Dados reais para o gráfico de Investimento x CPS (via endpoint)
  const chartData: ChartDataPoint[] = React.useMemo(() => {
    if (!investmentCpsData?.series) return [];
    return investmentCpsData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      investimento: Number(s.investment || 0),
      cps: Number(s.cps || 0),
      investimentoGoogle: Number(s.investment_google || 0),
      investimentoMeta: Number(s.investment_meta || 0),
    }));
  }, [investmentCpsData]);

  const handleInvestCpsChartOptionChange = (
    option: keyof typeof investCpsChartOptions,
  ) => {
    setInvestCpsChartOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  // Dados reais para o gráfico de Investimento x Receita Total (via endpoint)
  const revenueChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!investmentRevenueData?.series) return [];
    return investmentRevenueData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      investimento: Number(s.investment || 0),
      receita: Number(s.revenue || 0),
      investimentoGoogle: Number(s.investment_google || 0),
      investimentoMeta: Number(s.investment_meta || 0),
    }));
  }, [investmentRevenueData]);

  const handleInvestRevenueChartOptionChange = (
    option: keyof typeof investRevenueChartOptions,
  ) => {
    setInvestRevenueChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  // Dados reais para o gráfico de Investimento x Sessões (via endpoint)
  const sessionsChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!investmentSessionsData?.series) return [];
    return investmentSessionsData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      investimento: Number(s.investment || 0),
      sessoes: Number(s.sessions || 0),
      investimentoGoogle: Number(s.investment_google || 0),
      investimentoMeta: Number(s.investment_meta || 0),
    }));
  }, [investmentSessionsData]);

  const handleInvestSessionsChartOptionChange = (
    option: keyof typeof investSessionsChartOptions,
  ) => {
    setInvestSessionsChartOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }));
  };

  // Dados reais para o gráfico de Sessões x Taxa de Conversão (via endpoint)
  const conversionChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!sessionsConversionData?.series) return [];
    return sessionsConversionData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      sessoes: Number(s.sessions || 0),
      taxaConversao: Number(s.conversion_rate || 0),
    }));
  }, [sessionsConversionData]);

  // Dados reais para o gráfico de Sessões x Faturamento (via endpoint)
  const revenueSessionsChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!sessionsRevenueData?.series) return [];
    return sessionsRevenueData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      sessoes: Number(s.sessions || 0),
      faturamento: Number(s.revenue || 0),
    }));
  }, [sessionsRevenueData]);

  // Dados reais para o gráfico de Sessões x Ticket Médio (via endpoint)
  const ticketSessionsChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!sessionsAovData?.series) return [];
    return sessionsAovData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      sessoes: Number(s.sessions || 0),
      ticketMedio: Number(s.average_order_value || 0),
    }));
  }, [sessionsAovData]);

  // Dados reais para o gráfico de Sessões x ROAS (via endpoint)
  const roasSessionsChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!sessionsRoasData?.series) return [];
    return sessionsRoasData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      sessoes: Number(s.sessions || 0),
      roas: Number(s.roas || 0),
    }));
  }, [sessionsRoasData]);

  // Dados reais para o gráfico de Investimento x ROAS (via endpoint)
  const investmentRoasChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!investmentRoasData?.series) return [];
    return investmentRoasData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      investimento: Number(s.investment || 0),
      roas: Number(s.roas || 0),
      investimentoGoogle: Number(s.investment_google || 0),
      investimentoMeta: Number(s.investment_meta || 0),
    }));
  }, [investmentRoasData]);

  const handleInvestRoasChartOptionChange = (
    option: keyof typeof investRoasChartOptions,
  ) => {
    setInvestRoasChartOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  // Dados reais para o gráfico de Faturamento x Taxa de Conversão (via endpoint)
  const revenueConversionChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!revenueConversionData?.series) return [];
    return revenueConversionData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      faturamento: Number(s.revenue || 0),
      taxaConversao: Number(s.conversion_rate || 0),
    }));
  }, [revenueConversionData]);

  // Dados reais para o gráfico de Ticket Médio x Taxa de Conversão (via endpoint)
  const ticketConversionChartData: ChartDataPoint[] = React.useMemo(() => {
    if (!aovConversionData?.series) return [];
    return aovConversionData.series.map((s: ChartSeriesPoint) => ({
      month: formatMonthLabel(s),
      ticketMedio: Number(s.average_order_value || 0),
      taxaConversao: Number(s.conversion_rate || 0),
    }));
  }, [aovConversionData]);

  // Estado e paginação - Grupo de canais
  const [channelsData, setChannelsData] = useState<{
    items: MediaTableRow[];
    pagination: { page: number; page_size: number; total: number };
  }>({ items: [], pagination: { page: 1, page_size: 10, total: 0 } });
  // Começa ligado: a busca sai no efeito, e com `false` a primeira pintura dizia
  // "Nenhum dado encontrado" antes dela.
  const [channelsLoading, setChannelsLoading] = useState<boolean>(true);
  const [channelsPage, setChannelsPage] = useState<number>(1);
  const [channelsQuery, setChannelsQuery] = useState<TableQueryDTO>({});

  // Estado e paginação - Origem/Mídia
  const [sourcesData, setSourcesData] = useState<{
    items: MediaTableRow[];
    pagination: { page: number; page_size: number; total: number };
  }>({ items: [], pagination: { page: 1, page_size: 10, total: 0 } });
  const [sourcesLoading, setSourcesLoading] = useState<boolean>(true);
  const [sourcesPage, setSourcesPage] = useState<number>(1);
  const [sourcesQuery, setSourcesQuery] = useState<TableQueryDTO>({});

  // Reset de paginação quando filtro global mudar
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setChannelsPage(1);
    setSourcesPage(1);
  }, [filterState]);

  // Fetch das tabelas (server-side com query independente por tabela)
  useEffect(() => {
    const fetchTables = async () => {
      try {
        setChannelsLoading(true);
        setSourcesLoading(true);
        const startDate = formatDateToISO(filterState.dateRange.startDate);
        const endDate = formatDateToISO(filterState.dateRange.endDate);
        const comparison =
          filterState.comparison?.type === "previousYear"
            ? "previous_year"
            : "previous_period";
        const customerUuid = localStorage.getItem("active_customer_uuid");
        const accountId = undefined;
        if (!customerUuid) {
          throw new Error("Customer UUID não encontrado");
        }
        const { data: resp } =
          await getMediaPerformanceSessionsBreakdownApiV1MediaPerformanceSessionsBreakdownGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                comparison,
                customer_uuid: customerUuid,
                account_id: accountId,
              },
              throwOnError: true,
            },
          );
        setChannelsData(
          resp?.channels || {
            items: [],
            pagination: { page: channelsPage, page_size: 10, total: 0 },
          },
        );
        setSourcesData(
          resp?.sources || {
            items: [],
            pagination: { page: sourcesPage, page_size: 10, total: 0 },
          },
        );
      } catch (e) {
        setChannelsData({
          items: [],
          pagination: { page: 1, page_size: 10, total: 0 },
        });
        setSourcesData({
          items: [],
          pagination: { page: 1, page_size: 10, total: 0 },
        });
      } finally {
        setChannelsLoading(false);
        setSourcesLoading(false);
      }
    };
    fetchTables();
  }, [
    filterState,
    channelsPage,
    sourcesPage,
    channelsQuery,
    sourcesQuery,
    campaignFiltersNonce,
  ]);

  const onChannelsPageChange = (page: number) => setChannelsPage(page);
  const onSourcesPageChange = (page: number) => setSourcesPage(page);

  // Cálculos para o card lateral do primeiro gráfico (dados reais do endpoint)
  const investimentoChange =
    investmentCpsData?.summary?.investment_change_percentage ?? 0;
  const cpsChange = investmentCpsData?.summary?.cps_change_percentage ?? 0;

  // Cálculos para o card lateral do segundo gráfico (Receita)
  const hasRevenueData = revenueChartData.length >= 2;
  const currentMonthRevenue = hasRevenueData
    ? revenueChartData[revenueChartData.length - 1]
    : undefined;
  const previousMonthRevenue = hasRevenueData
    ? revenueChartData[revenueChartData.length - 2]
    : undefined;

  const investimentoChangeRevenue =
    hasRevenueData &&
    currentMonthRevenue?.investimento !== undefined &&
    previousMonthRevenue?.investimento
      ? ((currentMonthRevenue.investimento -
          previousMonthRevenue.investimento) /
          previousMonthRevenue.investimento) *
        100
      : 0;
  const receitaChange =
    hasRevenueData && previousMonthRevenue && previousMonthRevenue.receita
      ? ((currentMonthRevenue!.receita! - previousMonthRevenue.receita!) /
          previousMonthRevenue.receita!) *
        100
      : 0;

  // Cálculos para o card lateral do terceiro gráfico (Sessões)
  const currentMonthSessions = investmentSessionsData?.summary;
  const previousMonthSessionsChangeInvestment =
    investmentSessionsData?.summary?.investment_change_percentage ?? 0;
  const previousMonthSessionsChangeSessions =
    investmentSessionsData?.summary?.sessions_change_percentage ?? 0;

  // Cálculos para o card lateral do quarto gráfico (Conversão)
  const currentMonthConversion = sessionsConversionData?.summary;
  const sessoesChangeConversion =
    sessionsConversionData?.summary?.sessions_change_percentage ?? 0;
  const taxaConversaoChange =
    sessionsConversionData?.summary?.conversion_rate_change_percentage ?? 0;

  // Cálculos para o card lateral do quinto gráfico (Faturamento)
  const currentMonthRevenueSessions = sessionsRevenueData?.summary;
  const sessoesChangeRevenue =
    sessionsRevenueData?.summary?.sessions_change_percentage ?? 0;
  const faturamentoChange =
    sessionsRevenueData?.summary?.revenue_change_percentage ?? 0;

  // Cálculos para o card lateral do sexto gráfico (Ticket Médio)
  const currentMonthTicketSessions = sessionsAovData?.summary;
  const sessoesChangeTicket =
    sessionsAovData?.summary?.sessions_change_percentage ?? 0;
  const ticketMedioChange =
    sessionsAovData?.summary?.average_order_value_change_percentage ?? 0;

  // Cálculos para o card lateral do sétimo gráfico (ROAS) com dados reais
  const currentMonthRoasSessions = sessionsRoasData?.summary;
  const sessoesChangeRoas =
    sessionsRoasData?.summary?.sessions_change_percentage ?? 0;
  const roasChange = sessionsRoasData?.summary?.roas_change_percentage ?? 0;

  // Cálculos para o card lateral do gráfico de Investimento x ROAS
  const currentMonthInvestmentRoas = investmentRoasData?.summary;
  const investimentoChangeRoas =
    investmentRoasData?.summary?.investment_change_percentage ?? 0;
  const roasChangeInvest =
    investmentRoasData?.summary?.roas_change_percentage ?? 0;

  // Cálculos para o card lateral do oitavo gráfico (Faturamento x Conversão) com dados do endpoint
  const currentMonthRevenueConversion = revenueConversionData?.summary;
  const faturamentoChangeConversion =
    revenueConversionData?.summary?.revenue_change_percentage ?? 0;
  const taxaConversaoChangeFaturamento =
    revenueConversionData?.summary?.conversion_rate_change_percentage ?? 0;

  // Cálculos para o card lateral do nono gráfico (Ticket Médio x Conversão) - via endpoint
  const currentMonthTicketConversion = aovConversionData?.summary;
  const ticketMedioChangeConversion =
    aovConversionData?.summary?.average_order_value_change_percentage ?? 0;
  const taxaConversaoChangeTicket =
    aovConversionData?.summary?.conversion_rate_change_percentage ?? 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  /** Variação de comparação — o sinal vem no número, e não só da cor (item 34). */
  const formatPercentage = (value: number) => formatSignedPercent(value);

  const formatPercentValue = (value: number) =>
    `${Number(value ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

  const formatDecimalValue = (value: number, suffix = "") =>
    `${Number(value ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix}`;

  // Colunas para Grupo de Canais
  const channelGroupColumns: TableColumn[] = useMemo(
    () => [
      {
        key: "channel_group",
        header: "Grupo de Canais",
        align: "left" as const,
        alwaysVisible: true,
      },
      {
        key: "sessions",
        header: "Sessões",
        align: "right" as const,
        render: (value: number | string | null | undefined) =>
          formatNumber(toNumericValue(value)),
      },
      {
        key: "total_revenue",
        header: "Receita Total",
        align: "right" as const,
        render: (value: number | string | null | undefined) =>
          formatCurrency(toNumericValue(value)),
      },
      {
        key: "conversion_rate",
        header: "TX Conversão",
        align: "right" as const,
        visible: false,
        render: (value: number | string | null | undefined) =>
          formatDecimalValue(toNumericValue(value), "%"),
      },
    ],
    [],
  );

  // Colunas para Origem/Mídia
  const sourceMediaColumns: TableColumn[] = useMemo(
    () => [
      {
        key: "source_medium",
        header: "Origem / Mídia",
        align: "left" as const,
        alwaysVisible: true,
      },
      {
        key: "sessions",
        header: "Sessões",
        align: "right" as const,
        render: (value: number | string | null | undefined) =>
          formatNumber(toNumericValue(value)),
      },
      {
        key: "total_revenue",
        header: "Receita Total",
        align: "right" as const,
        render: (value: number | string | null | undefined) =>
          formatCurrency(toNumericValue(value)),
      },
      {
        key: "conversion_rate",
        header: "TX Conversão",
        align: "right" as const,
        visible: false,
        render: (value: number | string | null | undefined) =>
          formatDecimalValue(toNumericValue(value), "%"),
      },
    ],
    [],
  );

  /**
   * O número de um card lateral sem resposta: indicador enquanto a série está em voo, e N/D com
   * o motivo quando ela terminou sem ele. Antes o N/D saía também durante o carregamento.
   */
  const resumoAusente = (
    carregando: boolean,
    fontes: readonly RequisitoDeFonte[],
  ) =>
    carregando ? (
      <CarregandoValor className="text-2xl" />
    ) : (
      <ValorIndisponivel
        fontes={fontes}
        erro={erroDados}
        className="text-2xl font-bold text-muted-foreground"
      />
    );

  const renderMetricCard = (metric: MetricCard) => {
    const IconComponent = metric.icon;
    const isValid =
      metric.rawValue !== null &&
      metric.rawValue !== undefined &&
      Number.isFinite(metric.rawValue);
    const formatFn = (n: number) => {
      if (metric.dataType === "CURRENCY") {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(n));
      }
      if (metric.dataType === "PERCENT") {
        return `${Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
      }
      return new Intl.NumberFormat("pt-BR").format(Number(n));
    };
    return (
      <Card key={metric.title} className="card-kpi-primary">
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-1">
              <span className="truncate">{metric.title}</span>
              <HelpTooltip
                content={getKpiTooltip(metric.title)}
                triggerClassName="h-3.5 w-3.5 text-[9px]"
              />
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex flex-col items-start min-w-0">
                <div className="min-w-0 flex-1">
                  <EstadoDoValor
                    carregando={loadingCards}
                    disponivel={isValid}
                    fontes={FONTES.ga4}
                    erro={erroDados}
                    className="text-2xl font-light text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(metric.rawValue)}
                      format={formatFn}
                      className="font-light text-foreground"
                      maxFontPx={30}
                      breathingRoomPx={18}
                      ariaLabel={metric.title}
                    />
                  </EstadoDoValor>
                </div>
                {comparisonEnabled &&
                  !loadingCards &&
                  typeof metric.change === "number" && (
                    <div className="flex items-center gap-1 text-xs">
                      {metric.changeType === "up" ? (
                        <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                      ) : metric.changeType === "down" ? (
                        <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                      ) : null}
                      <span
                        className={
                          metric.changeType === "up"
                            ? "text-success-soft"
                            : metric.changeType === "down"
                              ? "text-error-soft"
                              : "text-muted-foreground"
                        }
                      >
                        {formatPercentage(metric.change)}
                      </span>
                    </div>
                  )}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <IconComponent className="h-6 w-6 text-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 page-background">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Performance Geral</h1>
          <p className="text-muted-foreground">
            Análise consolidada da performance geral e investimentos
          </p>
        </div>
        <Badge
          variant="outline"
          className="bg-primary text-primary-foreground border-primary"
        >
          Performance Geral
        </Badge>
      </div>

      <GlobalFilter
        rightSlot={
          <CampaignFiltersControl
            customerUuid={customerUuid}
            onSaved={handleCampaignFiltersSaved}
          />
        }
      />

      {/* Cards Iniciais - Primeira linha */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {metricsCards.map(renderMetricCard)}
      </div>

      {/* Gráfico e Card Lateral - Segunda linha */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6">
        {/* Gráfico - 9 colunas - Melhorado visualmente */}
        <Card className="lg:col-span-9 card-kpi-primary">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle>INVESTIMENTO X CPS</CardTitle>
                <CardDescription>
                  Descobrir se o aumento de investimento impacta no CPS
                </CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-muted-foreground"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Métricas
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuCheckboxItem
                    checked={investCpsChartOptions.investimento}
                    onCheckedChange={() =>
                      handleInvestCpsChartOptionChange("investimento")
                    }
                  >
                    Investimento (Meta + Google)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investCpsChartOptions.cps}
                    onCheckedChange={() =>
                      handleInvestCpsChartOptionChange("cps")
                    }
                  >
                    CPS
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investCpsChartOptions.investimentoGoogle}
                    onCheckedChange={() =>
                      handleInvestCpsChartOptionChange("investimentoGoogle")
                    }
                  >
                    Investimento Google
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investCpsChartOptions.investimentoMeta}
                    onCheckedChange={() =>
                      handleInvestCpsChartOptionChange("investimentoMeta")
                    }
                  >
                    Investimento Meta
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingInvestmentCps ? (
                <div className="h-full flex items-center justify-center">
                  <Skeleton className="h-8 w-3/4" />
                </div>
              ) : (
                (() => {
                  const hasAny =
                    investCpsChartOptions.investimento ||
                    investCpsChartOptions.cps ||
                    investCpsChartOptions.investimentoGoogle ||
                    investCpsChartOptions.investimentoMeta;
                  if (!hasAny)
                    return (
                      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                        Selecione ao menos uma métrica
                      </div>
                    );

                  const COLORS: Record<string, string> = {
                    investimento: "#64748b",
                    cps: "#14b8a6",
                    investimentoGoogle: "#10b981",
                    investimentoMeta: "#8b5cf6",
                  };
                  const LABELS: Record<string, string> = {
                    investimento: "Investimento (Meta + Google)",
                    cps: "CPS",
                    investimentoGoogle: "Investimento Google",
                    investimentoMeta: "Investimento Meta",
                  };

                  return (
                    <ScrollableChart minWidth="500px">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={chartData}
                          margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(0,0,0,0.06)"
                          />
                          <XAxis
                            dataKey="month"
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                          />

                          {/* Eixo Y Esquerdo: Investimento (valores em R$) */}
                          <YAxis
                            yAxisId="left"
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={60}
                            tickFormatter={(v: number) =>
                              v >= 1000
                                ? `${(v / 1000).toLocaleString("pt-BR")}k`
                                : String(v)
                            }
                          />

                          {/* Eixo Y Direito: CPS (valores menores em R$) */}
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fill: "#14b8a6", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={55}
                            tickFormatter={(v: number) =>
                              `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            }
                          />

                          <RechartsTooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                  <p className="text-gray-500 text-xs mb-1">
                                    {label}
                                  </p>
                                  <table style={{ borderSpacing: "8px 2px" }}>
                                    <tbody>
                                      {payload.map((entry) => {
                                        const key = entry.dataKey as string;
                                        const formatted = formatCurrency(
                                          Number(entry.value ?? 0),
                                        );
                                        return (
                                          <tr key={key}>
                                            <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                              <span
                                                className="inline-block w-2.5 h-2.5 rounded-full"
                                                style={{
                                                  backgroundColor:
                                                    COLORS[key] || entry.color,
                                                }}
                                              />
                                              {LABELS[key] || key}:
                                            </td>
                                            <td className="text-right font-semibold text-gray-900 pl-3">
                                              {formatted}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            }}
                          />

                          <RechartsLegend
                            formatter={(value: string) =>
                              LABELS[value] || value
                            }
                            wrapperStyle={{ fontSize: 12 }}
                          />

                          {/* Investimentos no eixo esquerdo */}
                          {investCpsChartOptions.investimento && (
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="investimento"
                              stroke={COLORS.investimento}
                              strokeWidth={2}
                              dot={false}
                              name="investimento"
                            />
                          )}
                          {investCpsChartOptions.investimentoGoogle && (
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="investimentoGoogle"
                              stroke={COLORS.investimentoGoogle}
                              strokeWidth={2}
                              dot={false}
                              name="investimentoGoogle"
                            />
                          )}
                          {investCpsChartOptions.investimentoMeta && (
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="investimentoMeta"
                              stroke={COLORS.investimentoMeta}
                              strokeWidth={2}
                              dot={false}
                              name="investimentoMeta"
                            />
                          )}

                          {/* CPS no eixo direito (valores menores) */}
                          {investCpsChartOptions.cps && (
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="cps"
                              stroke={COLORS.cps}
                              strokeWidth={2}
                              dot={false}
                              name="cps"
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </ScrollableChart>
                  );
                })()
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Investimento */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Investimento
                </span>
                <div className="flex items-center space-x-1">
                  {investimentoChange >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      investimentoChange >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(investimentoChange)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {investmentCpsData?.summary?.investment !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentCps}
                    disponivel
                    fontes={FONTES.investimento}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(investmentCpsData.summary.investment)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Investimento"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentCps, FONTES.investimento)
                )}
              </div>
            </div>

            {/* Segunda linha do card - CPS */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  CPS
                </span>
                <div className="flex items-center space-x-1">
                  {cpsChange >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      cpsChange >= 0 ? "text-success-soft" : "text-error-soft"
                    }
                  >
                    {formatPercentage(cpsChange)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {investmentCpsData?.summary?.cps !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentCps}
                    disponivel
                    fontes={FONTES.ga4ComMidia}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(investmentCpsData.summary.cps)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="CPS"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentCps, FONTES.ga4ComMidia)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico - Investimento x ROAS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9 card-kpi-primary">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle>INVESTIMENTO X ROAS</CardTitle>
                <CardDescription>
                  Entender se o aumento do investimento prejudica o ROAS
                </CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-muted-foreground"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Métricas
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuCheckboxItem
                    checked={investRoasChartOptions.investimento}
                    onCheckedChange={() =>
                      handleInvestRoasChartOptionChange("investimento")
                    }
                  >
                    Investimento (Meta + Google)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investRoasChartOptions.roas}
                    onCheckedChange={() =>
                      handleInvestRoasChartOptionChange("roas")
                    }
                  >
                    ROAS
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investRoasChartOptions.investimentoGoogle}
                    onCheckedChange={() =>
                      handleInvestRoasChartOptionChange("investimentoGoogle")
                    }
                  >
                    Investimento Google
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investRoasChartOptions.investimentoMeta}
                    onCheckedChange={() =>
                      handleInvestRoasChartOptionChange("investimentoMeta")
                    }
                  >
                    Investimento Meta
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingInvestmentRoas ? (
                <div className="h-full flex items-center justify-center">
                  <Skeleton className="h-8 w-3/4" />
                </div>
              ) : (
                (() => {
                  const hasAny =
                    investRoasChartOptions.investimento ||
                    investRoasChartOptions.roas ||
                    investRoasChartOptions.investimentoGoogle ||
                    investRoasChartOptions.investimentoMeta;
                  if (!hasAny)
                    return (
                      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                        Selecione ao menos uma métrica
                      </div>
                    );

                  const COLORS: Record<string, string> = {
                    investimento: "#64748b",
                    roas: "#ec4899",
                    investimentoGoogle: "#10b981",
                    investimentoMeta: "#8b5cf6",
                  };
                  const LABELS: Record<string, string> = {
                    investimento: "Investimento (Meta + Google)",
                    roas: "ROAS",
                    investimentoGoogle: "Investimento Google",
                    investimentoMeta: "Investimento Meta",
                  };

                  return (
                    <ScrollableChart minWidth="500px">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={investmentRoasChartData}
                          margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(0,0,0,0.06)"
                          />
                          <XAxis
                            dataKey="month"
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                          />

                          {/* Eixo Y Esquerdo: Investimento (R$) */}
                          <YAxis
                            yAxisId="left"
                            tick={{ fill: "#6b7280", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={60}
                            tickFormatter={(v: number) =>
                              v >= 1000
                                ? `${(v / 1000).toLocaleString("pt-BR")}k`
                                : String(v)
                            }
                          />

                          {/* Eixo Y Direito: ROAS (multiplicador) */}
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fill: "#ec4899", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={50}
                            tickFormatter={(v: number) =>
                              v.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            }
                          />

                          <RechartsTooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                  <p className="text-gray-500 text-xs mb-1">
                                    {label}
                                  </p>
                                  <table style={{ borderSpacing: "8px 2px" }}>
                                    <tbody>
                                      {payload.map((entry) => {
                                        const key = entry.dataKey as string;
                                        const formatted =
                                          key === "roas"
                                            ? Number(
                                                entry.value,
                                              ).toLocaleString("pt-BR", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              })
                                            : formatCurrency(
                                                Number(entry.value ?? 0),
                                              );
                                        return (
                                          <tr key={key}>
                                            <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                              <span
                                                className="inline-block w-2.5 h-2.5 rounded-full"
                                                style={{
                                                  backgroundColor:
                                                    COLORS[key] || entry.color,
                                                }}
                                              />
                                              {LABELS[key] || key}:
                                            </td>
                                            <td className="text-right font-semibold text-gray-900 pl-3">
                                              {formatted}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            }}
                          />

                          <RechartsLegend
                            formatter={(value: string) =>
                              LABELS[value] || value
                            }
                            wrapperStyle={{ fontSize: 12 }}
                          />

                          {/* Investimentos no eixo esquerdo */}
                          {investRoasChartOptions.investimento && (
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="investimento"
                              stroke={COLORS.investimento}
                              strokeWidth={2}
                              dot={false}
                              name="investimento"
                            />
                          )}
                          {investRoasChartOptions.investimentoGoogle && (
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="investimentoGoogle"
                              stroke={COLORS.investimentoGoogle}
                              strokeWidth={2}
                              dot={false}
                              name="investimentoGoogle"
                            />
                          )}
                          {investRoasChartOptions.investimentoMeta && (
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="investimentoMeta"
                              stroke={COLORS.investimentoMeta}
                              strokeWidth={2}
                              dot={false}
                              name="investimentoMeta"
                            />
                          )}

                          {/* ROAS no eixo direito */}
                          {investRoasChartOptions.roas && (
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="roas"
                              stroke={COLORS.roas}
                              strokeWidth={2}
                              dot={false}
                              name="roas"
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </ScrollableChart>
                  );
                })()
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Investimento */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Investimento
                </span>
                <div className="flex items-center space-x-1">
                  {investimentoChangeRoas >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      investimentoChangeRoas >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(investimentoChangeRoas)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {investmentRoasData?.summary?.investment !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentRoas}
                    disponivel
                    fontes={FONTES.investimento}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(investmentRoasData.summary.investment)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Investimento"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentRoas, FONTES.investimento)
                )}
              </div>
            </div>

            {/* Segunda linha do card - ROAS */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  ROAS
                </span>
                <div className="flex items-center space-x-1">
                  {roasChangeInvest >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      roasChangeInvest >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(roasChangeInvest)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {investmentRoasData?.summary?.roas !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentRoas}
                    disponivel
                    fontes={FONTES.ga4ComMidia}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(investmentRoasData.summary.roas)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="ROAS"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentRoas, FONTES.ga4ComMidia)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Segundo Gráfico - Investimento x Receita Total */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9 card-kpi-primary">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle>INVESTIMENTO X RECEITA TOTAL</CardTitle>
                <CardDescription>
                  Analisar se o faturamento segue na mesma proporção do
                  investimento
                </CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-muted-foreground"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Métricas
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuCheckboxItem
                    checked={investRevenueChartOptions.investimento}
                    onCheckedChange={() =>
                      handleInvestRevenueChartOptionChange("investimento")
                    }
                  >
                    Investimento (Meta + Google)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investRevenueChartOptions.receita}
                    onCheckedChange={() =>
                      handleInvestRevenueChartOptionChange("receita")
                    }
                  >
                    Receita Total
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investRevenueChartOptions.investimentoGoogle}
                    onCheckedChange={() =>
                      handleInvestRevenueChartOptionChange("investimentoGoogle")
                    }
                  >
                    Investimento Google
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investRevenueChartOptions.investimentoMeta}
                    onCheckedChange={() =>
                      handleInvestRevenueChartOptionChange("investimentoMeta")
                    }
                  >
                    Investimento Meta
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {(() => {
                const hasAny =
                  investRevenueChartOptions.investimento ||
                  investRevenueChartOptions.receita ||
                  investRevenueChartOptions.investimentoGoogle ||
                  investRevenueChartOptions.investimentoMeta;
                if (!hasAny)
                  return (
                    <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                      Selecione ao menos uma métrica
                    </div>
                  );

                const COLORS: Record<string, string> = {
                  investimento: "#64748b",
                  receita: "#10b981",
                  investimentoGoogle: "#f59e0b",
                  investimentoMeta: "#8b5cf6",
                };
                const LABELS: Record<string, string> = {
                  investimento: "Investimento (Meta + Google)",
                  receita: "Receita Total",
                  investimentoGoogle: "Investimento Google",
                  investimentoMeta: "Investimento Meta",
                };

                return (
                  <ScrollableChart minWidth="500px">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={revenueChartData}
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(0,0,0,0.06)"
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                        />

                        {/* Eixo Y Esquerdo: Investimento (R$) */}
                        <YAxis
                          yAxisId="left"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={60}
                          tickFormatter={(v: number) =>
                            v >= 1000
                              ? `${(v / 1000).toLocaleString("pt-BR")}k`
                              : String(v)
                          }
                        />

                        {/* Eixo Y Direito: Receita Total (R$, escala maior) */}
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fill: "#10b981", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={65}
                          tickFormatter={(v: number) =>
                            v >= 1000
                              ? `R$ ${(v / 1000).toLocaleString("pt-BR")}k`
                              : `R$ ${v}`
                          }
                        />

                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                <p className="text-gray-500 text-xs mb-1">
                                  {label}
                                </p>
                                <table style={{ borderSpacing: "8px 2px" }}>
                                  <tbody>
                                    {payload.map((entry) => {
                                      const key = entry.dataKey as string;
                                      return (
                                        <tr key={key}>
                                          <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                            <span
                                              className="inline-block w-2.5 h-2.5 rounded-full"
                                              style={{
                                                backgroundColor:
                                                  COLORS[key] || entry.color,
                                              }}
                                            />
                                            {LABELS[key] || key}:
                                          </td>
                                          <td className="text-right font-semibold text-gray-900 pl-3">
                                            {formatCurrency(
                                              Number(entry.value ?? 0),
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          }}
                        />

                        <RechartsLegend
                          formatter={(value: string) => LABELS[value] || value}
                          wrapperStyle={{ fontSize: 12 }}
                        />

                        {/* Investimentos no eixo esquerdo */}
                        {investRevenueChartOptions.investimento && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="investimento"
                            stroke={COLORS.investimento}
                            strokeWidth={2}
                            dot={false}
                            name="investimento"
                          />
                        )}
                        {investRevenueChartOptions.investimentoGoogle && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="investimentoGoogle"
                            stroke={COLORS.investimentoGoogle}
                            strokeWidth={2}
                            dot={false}
                            name="investimentoGoogle"
                          />
                        )}
                        {investRevenueChartOptions.investimentoMeta && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="investimentoMeta"
                            stroke={COLORS.investimentoMeta}
                            strokeWidth={2}
                            dot={false}
                            name="investimentoMeta"
                          />
                        )}

                        {/* Receita no eixo direito (escala maior) */}
                        {investRevenueChartOptions.receita && (
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="receita"
                            stroke={COLORS.receita}
                            strokeWidth={2}
                            dot={false}
                            name="receita"
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ScrollableChart>
                );
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Investimento */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Investimento
                </span>
                <div className="flex items-center space-x-1">
                  {investimentoChangeRevenue >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      investimentoChangeRevenue >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(investimentoChangeRevenue)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {investmentRevenueData?.summary?.investment !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentRevenue}
                    disponivel
                    fontes={FONTES.investimento}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(investmentRevenueData.summary.investment)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Investimento"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentRevenue, FONTES.investimento)
                )}
              </div>
            </div>

            {/* Segunda linha do card - Receita Total */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Receita Total
                </span>
                <div className="flex items-center space-x-1">
                  {receitaChange >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      receitaChange >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(receitaChange)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {investmentRevenueData?.summary?.revenue !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentRevenue}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(investmentRevenueData.summary.revenue)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Receita total"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentRevenue, FONTES.ga4)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Terceiro Gráfico - Investimento x Sessões */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9 card-kpi-primary">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle>INVESTIMENTO X SESSÕES</CardTitle>
                <CardDescription>
                  Analisar se o investimento está trazendo tráfego qualificado
                </CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-muted-foreground"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Métricas
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuCheckboxItem
                    checked={investSessionsChartOptions.investimento}
                    onCheckedChange={() =>
                      handleInvestSessionsChartOptionChange("investimento")
                    }
                  >
                    Investimento (Meta + Google)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investSessionsChartOptions.sessoes}
                    onCheckedChange={() =>
                      handleInvestSessionsChartOptionChange("sessoes")
                    }
                  >
                    Sessões
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investSessionsChartOptions.investimentoGoogle}
                    onCheckedChange={() =>
                      handleInvestSessionsChartOptionChange(
                        "investimentoGoogle",
                      )
                    }
                  >
                    Investimento Google
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={investSessionsChartOptions.investimentoMeta}
                    onCheckedChange={() =>
                      handleInvestSessionsChartOptionChange("investimentoMeta")
                    }
                  >
                    Investimento Meta
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {(() => {
                const hasAny =
                  investSessionsChartOptions.investimento ||
                  investSessionsChartOptions.sessoes ||
                  investSessionsChartOptions.investimentoGoogle ||
                  investSessionsChartOptions.investimentoMeta;
                if (!hasAny)
                  return (
                    <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                      Selecione ao menos uma métrica
                    </div>
                  );

                const COLORS: Record<string, string> = {
                  investimento: "#64748b",
                  sessoes: "#10b981",
                  investimentoGoogle: "#f59e0b",
                  investimentoMeta: "#8b5cf6",
                };
                const LABELS: Record<string, string> = {
                  investimento: "Investimento (Meta + Google)",
                  sessoes: "Sessões",
                  investimentoGoogle: "Investimento Google",
                  investimentoMeta: "Investimento Meta",
                };

                return (
                  <ScrollableChart minWidth="500px">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={sessionsChartData}
                        margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(0,0,0,0.06)"
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                        />

                        {/* Eixo Y Esquerdo: Investimento (R$) */}
                        <YAxis
                          yAxisId="left"
                          tick={{ fill: "#6b7280", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={60}
                          tickFormatter={(v: number) =>
                            v >= 1000
                              ? `${(v / 1000).toLocaleString("pt-BR")}k`
                              : String(v)
                          }
                        />

                        {/* Eixo Y Direito: Sessões (escala numérica) */}
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fill: "#10b981", fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={55}
                          tickFormatter={(v: number) =>
                            v >= 1000
                              ? `${(v / 1000).toLocaleString("pt-BR")}k`
                              : String(v)
                          }
                        />

                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                <p className="text-gray-500 text-xs mb-1">
                                  {label}
                                </p>
                                <table style={{ borderSpacing: "8px 2px" }}>
                                  <tbody>
                                    {payload.map((entry) => {
                                      const key = entry.dataKey as string;
                                      const isCurrency = key !== "sessoes";
                                      return (
                                        <tr key={key}>
                                          <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                            <span
                                              className="inline-block w-2.5 h-2.5 rounded-full"
                                              style={{
                                                backgroundColor:
                                                  COLORS[key] || entry.color,
                                              }}
                                            />
                                            {LABELS[key] || key}:
                                          </td>
                                          <td className="text-right font-semibold text-gray-900 pl-3">
                                            {isCurrency
                                              ? formatCurrency(
                                                  Number(entry.value ?? 0),
                                                )
                                              : Number(
                                                  entry.value,
                                                ).toLocaleString("pt-BR")}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          }}
                        />

                        <RechartsLegend
                          formatter={(value: string) => LABELS[value] || value}
                          wrapperStyle={{ fontSize: 12 }}
                        />

                        {/* Investimentos no eixo esquerdo */}
                        {investSessionsChartOptions.investimento && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="investimento"
                            stroke={COLORS.investimento}
                            strokeWidth={2}
                            dot={false}
                            name="investimento"
                          />
                        )}
                        {investSessionsChartOptions.investimentoGoogle && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="investimentoGoogle"
                            stroke={COLORS.investimentoGoogle}
                            strokeWidth={2}
                            dot={false}
                            name="investimentoGoogle"
                          />
                        )}
                        {investSessionsChartOptions.investimentoMeta && (
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="investimentoMeta"
                            stroke={COLORS.investimentoMeta}
                            strokeWidth={2}
                            dot={false}
                            name="investimentoMeta"
                          />
                        )}

                        {/* Sessões no eixo direito */}
                        {investSessionsChartOptions.sessoes && (
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="sessoes"
                            stroke={COLORS.sessoes}
                            strokeWidth={2}
                            dot={false}
                            name="sessoes"
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ScrollableChart>
                );
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Investimento */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Investimento
                </span>
                <div className="flex items-center space-x-1">
                  {previousMonthSessionsChangeInvestment >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      previousMonthSessionsChangeInvestment >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(previousMonthSessionsChangeInvestment)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthSessions?.investment !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentSessions}
                    disponivel
                    fontes={FONTES.investimento}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthSessions.investment)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Investimento"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentSessions, FONTES.investimento)
                )}
              </div>
            </div>

            {/* Segunda linha do card - Sessões */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Sessões
                </span>
                <div className="flex items-center space-x-1">
                  {previousMonthSessionsChangeSessions >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      previousMonthSessionsChangeSessions >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(previousMonthSessionsChangeSessions)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthSessions?.sessions !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingInvestmentSessions}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthSessions.sessions)}
                      format={(n) =>
                        new Intl.NumberFormat("pt-BR").format(Math.trunc(n))
                      }
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Sessões"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingInvestmentSessions, FONTES.ga4)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabelas Complementares - Mesma Linha */}
      <div className="mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6">
          <StandardizedTable
            title="Grupo de Canais Padrão da Sessão"
            description="Distribuição de sessões e receita por grupo de canais"
            columns={channelGroupColumns}
            data={channelsData.items || []}
            loading={channelsLoading}
            pagination={{
              current: channelsPage,
              pageSize: channelsData?.pagination?.page_size || 10,
              total: channelsData?.pagination?.total || 0,
              onPageChange: onChannelsPageChange,
            }}
            queryMode="server"
            onQueryChange={(q) => {
              setChannelsQuery(q);
              setChannelsPage(1);
            }}
            className="lg:col-span-6"
          />
          <StandardizedTable
            title="Origem / Mídia da Sessão"
            description="Detalhamento de sessões e receita por origem/mídia específica"
            columns={sourceMediaColumns}
            data={sourcesData.items || []}
            loading={sourcesLoading}
            pagination={{
              current: sourcesPage,
              pageSize: sourcesData?.pagination?.page_size || 10,
              total: sourcesData?.pagination?.total || 0,
              onPageChange: onSourcesPageChange,
            }}
            queryMode="server"
            onQueryChange={(q) => {
              setSourcesQuery(q);
              setSourcesPage(1);
            }}
            className="lg:col-span-6"
          />
        </div>
      </div>

      {/* Quarto Gráfico - Sessões x Taxa de Conversão (Abaixo das Tabelas) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9">
          <CardHeader>
            <CardTitle>Sessões x Taxa de Conversão</CardTitle>
            <CardDescription>
              Entender se o aumento de tráfego mantém a taxa de conversão
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingSessionsConversion ? (
                <div className="h-full flex items-center justify-center">
                  <Skeleton className="h-8 w-3/4" />
                </div>
              ) : (
                <ScrollableChart minWidth="500px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={conversionChartData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(0,0,0,0.06)"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={55}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `${(v / 1000).toLocaleString("pt-BR")}k`
                            : String(v)
                        }
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#10b981", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={50}
                        tickFormatter={(v: number) =>
                          `${v.toLocaleString("pt-BR")}%`
                        }
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const LABELS: Record<string, string> = {
                            sessoes: "Sessões",
                            taxaConversao: "Taxa de Conversão",
                          };
                          const COLORS: Record<string, string> = {
                            sessoes: "#64748b",
                            taxaConversao: "#10b981",
                          };
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                              <p className="text-gray-500 text-xs mb-1">
                                {label}
                              </p>
                              <table style={{ borderSpacing: "8px 2px" }}>
                                <tbody>
                                  {payload.map((entry) => (
                                    <tr key={String(entry.dataKey ?? "")}>
                                      <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                        <span
                                          className="inline-block w-2.5 h-2.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              COLORS[
                                                String(entry.dataKey ?? "")
                                              ] || entry.color,
                                          }}
                                        />
                                        {LABELS[String(entry.dataKey ?? "")] ||
                                          entry.dataKey}
                                        :
                                      </td>
                                      <td className="text-right font-semibold text-gray-900 pl-3">
                                        {entry.dataKey === "taxaConversao"
                                          ? `${Number(entry.value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
                                          : Number(entry.value).toLocaleString(
                                              "pt-BR",
                                            )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }}
                      />
                      <RechartsLegend
                        formatter={(value: string) =>
                          ({
                            sessoes: "Sessões",
                            taxaConversao: "Taxa de Conversão",
                          })[value] || value
                        }
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="sessoes"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={false}
                        name="sessoes"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="taxaConversao"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="taxaConversao"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ScrollableChart>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Sessões */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Sessões
                </span>
                <div className="flex items-center space-x-1">
                  {sessoesChangeConversion >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      sessoesChangeConversion >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(sessoesChangeConversion)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthConversion?.sessions !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingSessionsConversion}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthConversion.sessions)}
                      format={(n) =>
                        new Intl.NumberFormat("pt-BR").format(Math.trunc(n))
                      }
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Sessões"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsConversion, FONTES.ga4)
                )}
              </div>
            </div>

            {/* Segunda linha do card - Taxa de Conversão */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Taxa de Conversão
                </span>
                <div className="flex items-center space-x-1">
                  {taxaConversaoChange >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      taxaConversaoChange >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(taxaConversaoChange)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthConversion?.conversion_rate !== undefined ? (
                  <EstadoDoValor
                    carregando={loadingSessionsConversion}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthConversion.conversion_rate)}
                      format={(n) => formatPercentValue(Number(n))}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Taxa de conversão"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsConversion, FONTES.ga4)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quinto Gráfico - Sessões x Faturamento */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9">
          <CardHeader>
            <CardTitle>Sessões x Faturamento</CardTitle>
            <CardDescription>
              Entender se o faturamento acompanhou o aumento de sessões
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingSessionsRevenue ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <ScrollableChart minWidth="500px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={revenueSessionsChartData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(0,0,0,0.06)"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={55}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `${(v / 1000).toLocaleString("pt-BR")}k`
                            : String(v)
                        }
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#10b981", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={65}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `R$ ${(v / 1000).toLocaleString("pt-BR")}k`
                            : `R$ ${v}`
                        }
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const LABELS: Record<string, string> = {
                            sessoes: "Sessões",
                            faturamento: "Faturamento",
                          };
                          const COLORS: Record<string, string> = {
                            sessoes: "#64748b",
                            faturamento: "#10b981",
                          };
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                              <p className="text-gray-500 text-xs mb-1">
                                {label}
                              </p>
                              <table style={{ borderSpacing: "8px 2px" }}>
                                <tbody>
                                  {payload.map((entry) => (
                                    <tr key={String(entry.dataKey ?? "")}>
                                      <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                        <span
                                          className="inline-block w-2.5 h-2.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              COLORS[
                                                String(entry.dataKey ?? "")
                                              ] || entry.color,
                                          }}
                                        />
                                        {LABELS[String(entry.dataKey ?? "")] ||
                                          entry.dataKey}
                                        :
                                      </td>
                                      <td className="text-right font-semibold text-gray-900 pl-3">
                                        {entry.dataKey === "faturamento"
                                          ? formatCurrency(
                                              Number(entry.value ?? 0),
                                            )
                                          : Number(entry.value).toLocaleString(
                                              "pt-BR",
                                            )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }}
                      />
                      <RechartsLegend
                        formatter={(value: string) =>
                          ({
                            sessoes: "Sessões",
                            faturamento: "Faturamento",
                          })[value] || value
                        }
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="sessoes"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={false}
                        name="sessoes"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="faturamento"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="faturamento"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ScrollableChart>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Sessões */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Sessões
                </span>
                <div className="flex items-center space-x-1">
                  {sessoesChangeRevenue >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      sessoesChangeRevenue >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(sessoesChangeRevenue)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthRevenueSessions &&
                Number(currentMonthRevenueSessions.sessions) !== 0 ? (
                  <EstadoDoValor
                    carregando={loadingSessionsRevenue}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthRevenueSessions.sessions)}
                      format={(n) =>
                        new Intl.NumberFormat("pt-BR").format(Math.trunc(n))
                      }
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Sessões"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsRevenue, FONTES.ga4)
                )}
              </div>
            </div>

            {/* Segunda linha do card - Faturamento */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Faturamento
                </span>
                <div className="flex items-center space-x-1">
                  {faturamentoChange >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      faturamentoChange >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(faturamentoChange)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthRevenueSessions &&
                Number(currentMonthRevenueSessions.revenue || 0) !== 0 ? (
                  <EstadoDoValor
                    carregando={loadingSessionsRevenue}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthRevenueSessions.revenue || 0)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Faturamento"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsRevenue, FONTES.ga4)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sexto Gráfico - Sessões x Ticket Médio */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9">
          <CardHeader>
            <CardTitle>Sessões x Ticket Médio</CardTitle>
            <CardDescription>
              Entender se o aumento de sessões afetou o ticket médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingSessionsAov ? (
                <div className="h-full flex items-center justify-center">
                  <Skeleton className="h-8 w-3/4" />
                </div>
              ) : (
                <ScrollableChart minWidth="500px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={ticketSessionsChartData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(0,0,0,0.06)"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={55}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `${(v / 1000).toLocaleString("pt-BR")}k`
                            : String(v)
                        }
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#10b981", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={65}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `R$ ${(v / 1000).toLocaleString("pt-BR")}k`
                            : `R$ ${v}`
                        }
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const LABELS: Record<string, string> = {
                            sessoes: "Sessões",
                            ticketMedio: "Ticket Médio",
                          };
                          const COLORS: Record<string, string> = {
                            sessoes: "#64748b",
                            ticketMedio: "#10b981",
                          };
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                              <p className="text-gray-500 text-xs mb-1">
                                {label}
                              </p>
                              <table style={{ borderSpacing: "8px 2px" }}>
                                <tbody>
                                  {payload.map((entry) => (
                                    <tr key={String(entry.dataKey ?? "")}>
                                      <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                        <span
                                          className="inline-block w-2.5 h-2.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              COLORS[
                                                String(entry.dataKey ?? "")
                                              ] || entry.color,
                                          }}
                                        />
                                        {LABELS[String(entry.dataKey ?? "")] ||
                                          entry.dataKey}
                                        :
                                      </td>
                                      <td className="text-right font-semibold text-gray-900 pl-3">
                                        {entry.dataKey === "ticketMedio"
                                          ? formatCurrency(
                                              Number(entry.value ?? 0),
                                            )
                                          : Number(entry.value).toLocaleString(
                                              "pt-BR",
                                            )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }}
                      />
                      <RechartsLegend
                        formatter={(value: string) =>
                          ({
                            sessoes: "Sessões",
                            ticketMedio: "Ticket Médio",
                          })[value] || value
                        }
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="sessoes"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={false}
                        name="sessoes"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="ticketMedio"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="ticketMedio"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ScrollableChart>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Sessões */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Sessões
                </span>
                <div className="flex items-center space-x-1">
                  {sessoesChangeTicket >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      sessoesChangeTicket >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(sessoesChangeTicket)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthTicketSessions &&
                Number(currentMonthTicketSessions.sessions || 0) !== 0 ? (
                  <EstadoDoValor
                    carregando={loadingSessionsAov}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthTicketSessions.sessions)}
                      format={(n) =>
                        new Intl.NumberFormat("pt-BR").format(Math.trunc(n))
                      }
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Sessões"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsAov, FONTES.ga4)
                )}
              </div>
            </div>

            {/* Segunda linha do card - Ticket Médio */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Ticket Médio
                </span>
                <div className="flex items-center space-x-1">
                  {ticketMedioChange >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      ticketMedioChange >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(ticketMedioChange)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthTicketSessions &&
                Number(currentMonthTicketSessions.average_order_value || 0) !==
                  0 ? (
                  <EstadoDoValor
                    carregando={loadingSessionsAov}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(
                        currentMonthTicketSessions.average_order_value,
                      )}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Ticket médio"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsAov, FONTES.ga4)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sétimo Gráfico - Sessões x ROAS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9 ">
          <CardHeader>
            <CardTitle>Sessões x ROAS</CardTitle>
            <CardDescription>
              Entender se o aumento das sessões afetou o ROAS
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingSessionsRoas ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <ScrollableChart minWidth="500px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={roasSessionsChartData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(0,0,0,0.06)"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={55}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `${(v / 1000).toLocaleString("pt-BR")}k`
                            : String(v)
                        }
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#10b981", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={50}
                        tickFormatter={(v: number) =>
                          `${v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x`
                        }
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const LABELS: Record<string, string> = {
                            sessoes: "Sessões",
                            roas: "ROAS",
                          };
                          const COLORS: Record<string, string> = {
                            sessoes: "#64748b",
                            roas: "#10b981",
                          };
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                              <p className="text-gray-500 text-xs mb-1">
                                {label}
                              </p>
                              <table style={{ borderSpacing: "8px 2px" }}>
                                <tbody>
                                  {payload.map((entry) => (
                                    <tr key={String(entry.dataKey ?? "")}>
                                      <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                        <span
                                          className="inline-block w-2.5 h-2.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              COLORS[
                                                String(entry.dataKey ?? "")
                                              ] || entry.color,
                                          }}
                                        />
                                        {LABELS[String(entry.dataKey ?? "")] ||
                                          entry.dataKey}
                                        :
                                      </td>
                                      <td className="text-right font-semibold text-gray-900 pl-3">
                                        {entry.dataKey === "roas"
                                          ? `${Number(entry.value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`
                                          : Number(entry.value).toLocaleString(
                                              "pt-BR",
                                            )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }}
                      />
                      <RechartsLegend
                        formatter={(value: string) =>
                          ({ sessoes: "Sessões", roas: "ROAS" })[value] || value
                        }
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="sessoes"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={false}
                        name="sessoes"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="roas"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="roas"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ScrollableChart>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Sessões */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Sessões
                </span>
                <div className="flex items-center space-x-1">
                  {sessoesChangeRoas >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      sessoesChangeRoas >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(sessoesChangeRoas)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthRoasSessions?.sessions !== undefined &&
                currentMonthRoasSessions?.sessions !== null &&
                Number(currentMonthRoasSessions.sessions) !== 0 ? (
                  <EstadoDoValor
                    carregando={loadingSessionsRoas}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthRoasSessions.sessions)}
                      format={(n) =>
                        new Intl.NumberFormat("pt-BR").format(Math.trunc(n))
                      }
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Sessões"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsRoas, FONTES.ga4)
                )}
              </div>
            </div>

            {/* Segunda linha do card - ROAS */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  ROAS
                </span>
                <div className="flex items-center space-x-1">
                  {roasChange >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      roasChange >= 0 ? "text-success-soft" : "text-error-soft"
                    }
                  >
                    {formatPercentage(roasChange)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthRoasSessions?.roas !== undefined &&
                currentMonthRoasSessions?.roas !== null &&
                Number(currentMonthRoasSessions.roas) !== 0 ? (
                  <EstadoDoValor
                    carregando={loadingSessionsRoas}
                    disponivel
                    fontes={FONTES.ga4ComMidia}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthRoasSessions.roas)}
                      format={(n) => formatDecimalValue(Number(n), "x")}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="ROAS"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingSessionsRoas, FONTES.ga4ComMidia)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Oitavo Gráfico - Faturamento x Taxa de Conversão */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9">
          <CardHeader>
            <CardTitle>Faturamento x Taxa de Conversão</CardTitle>
            <CardDescription>
              Entender se aumentar o faturamento faz cair a taxa de conversão
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingRevenueConversion ? (
                <div className="h-full flex items-center justify-center">
                  <Skeleton className="h-8 w-3/4" />
                </div>
              ) : (
                <ScrollableChart minWidth="500px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={revenueConversionChartData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(0,0,0,0.06)"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={65}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `R$ ${(v / 1000).toLocaleString("pt-BR")}k`
                            : `R$ ${v}`
                        }
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#10b981", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={50}
                        tickFormatter={(v: number) =>
                          `${v.toLocaleString("pt-BR")}%`
                        }
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const LABELS: Record<string, string> = {
                            faturamento: "Faturamento",
                            taxaConversao: "Taxa de Conversão",
                          };
                          const COLORS: Record<string, string> = {
                            faturamento: "#64748b",
                            taxaConversao: "#10b981",
                          };
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                              <p className="text-gray-500 text-xs mb-1">
                                {label}
                              </p>
                              <table style={{ borderSpacing: "8px 2px" }}>
                                <tbody>
                                  {payload.map((entry) => (
                                    <tr key={String(entry.dataKey ?? "")}>
                                      <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                        <span
                                          className="inline-block w-2.5 h-2.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              COLORS[
                                                String(entry.dataKey ?? "")
                                              ] || entry.color,
                                          }}
                                        />
                                        {LABELS[String(entry.dataKey ?? "")] ||
                                          entry.dataKey}
                                        :
                                      </td>
                                      <td className="text-right font-semibold text-gray-900 pl-3">
                                        {entry.dataKey === "faturamento"
                                          ? formatCurrency(
                                              Number(entry.value ?? 0),
                                            )
                                          : `${Number(entry.value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }}
                      />
                      <RechartsLegend
                        formatter={(value: string) =>
                          ({
                            faturamento: "Faturamento",
                            taxaConversao: "Taxa de Conversão",
                          })[value] || value
                        }
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="faturamento"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={false}
                        name="faturamento"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="taxaConversao"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="taxaConversao"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ScrollableChart>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Faturamento */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Faturamento
                </span>
                <div className="flex items-center space-x-1">
                  {faturamentoChangeConversion >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      faturamentoChangeConversion >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(faturamentoChangeConversion)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthRevenueConversion &&
                Number(currentMonthRevenueConversion.revenue || 0) !== 0 ? (
                  <EstadoDoValor
                    carregando={loadingRevenueConversion}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(currentMonthRevenueConversion.revenue || 0)}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Faturamento"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingRevenueConversion, FONTES.ga4)
                )}
              </div>
            </div>

            {/* Segunda linha do card - Taxa de Conversão */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Taxa de Conversão
                </span>
                <div className="flex items-center space-x-1">
                  {taxaConversaoChangeFaturamento >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      taxaConversaoChangeFaturamento >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(taxaConversaoChangeFaturamento)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthRevenueConversion &&
                Number(currentMonthRevenueConversion.conversion_rate || 0) !==
                  0 ? (
                  <EstadoDoValor
                    carregando={loadingRevenueConversion}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(
                        currentMonthRevenueConversion.conversion_rate || 0,
                      )}
                      format={(n) => formatPercentValue(Number(n))}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Taxa de conversão"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingRevenueConversion, FONTES.ga4)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Nono Gráfico - Ticket Médio x Taxa de Conversão */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-6 mt-6">
        {/* Gráfico - 9 colunas */}
        <Card className="lg:col-span-9">
          <CardHeader>
            <CardTitle>Ticket Médio x Taxa de Conversão</CardTitle>
            <CardDescription>
              Equilíbrio entre valor do pedido e frequência de conversão
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {loadingAovConversion ? (
                <div className="h-full flex items-center justify-center">
                  <Skeleton className="h-8 w-3/4" />
                </div>
              ) : (
                <ScrollableChart minWidth="500px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={ticketConversionChartData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(0,0,0,0.06)"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={65}
                        tickFormatter={(v: number) =>
                          v >= 1000
                            ? `R$ ${(v / 1000).toLocaleString("pt-BR")}k`
                            : `R$ ${v}`
                        }
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#10b981", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        width={50}
                        tickFormatter={(v: number) =>
                          `${v.toLocaleString("pt-BR")}%`
                        }
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const LABELS: Record<string, string> = {
                            ticketMedio: "Ticket Médio",
                            taxaConversao: "Taxa de Conversão",
                          };
                          const COLORS: Record<string, string> = {
                            ticketMedio: "#64748b",
                            taxaConversao: "#10b981",
                          };
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                              <p className="text-gray-500 text-xs mb-1">
                                {label}
                              </p>
                              <table style={{ borderSpacing: "8px 2px" }}>
                                <tbody>
                                  {payload.map((entry) => (
                                    <tr key={String(entry.dataKey ?? "")}>
                                      <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                        <span
                                          className="inline-block w-2.5 h-2.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              COLORS[
                                                String(entry.dataKey ?? "")
                                              ] || entry.color,
                                          }}
                                        />
                                        {LABELS[String(entry.dataKey ?? "")] ||
                                          entry.dataKey}
                                        :
                                      </td>
                                      <td className="text-right font-semibold text-gray-900 pl-3">
                                        {entry.dataKey === "ticketMedio"
                                          ? formatCurrency(
                                              Number(entry.value ?? 0),
                                            )
                                          : `${Number(entry.value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }}
                      />
                      <RechartsLegend
                        formatter={(value: string) =>
                          ({
                            ticketMedio: "Ticket Médio",
                            taxaConversao: "Taxa de Conversão",
                          })[value] || value
                        }
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="ticketMedio"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={false}
                        name="ticketMedio"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="taxaConversao"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="taxaConversao"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ScrollableChart>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card Lateral - 3 colunas */}
        <Card className="lg:col-span-3 card-kpi-secondary">
          <CardHeader>
            <CardTitle>Resumo do Período</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primeira linha do card - Ticket Médio */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Ticket Médio
                </span>
                <div className="flex items-center space-x-1">
                  {ticketMedioChangeConversion >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      ticketMedioChangeConversion >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(ticketMedioChangeConversion)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthTicketConversion &&
                Number(
                  currentMonthTicketConversion.average_order_value || 0,
                ) !== 0 ? (
                  <EstadoDoValor
                    carregando={loadingAovConversion}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(
                        currentMonthTicketConversion.average_order_value || 0,
                      )}
                      format={(n) => formatCurrency(n)}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Ticket médio"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingAovConversion, FONTES.ga4)
                )}
              </div>
            </div>

            {/* Segunda linha do card - Taxa de Conversão */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Taxa de Conversão
                </span>
                <div className="flex items-center space-x-1">
                  {taxaConversaoChangeTicket >= 0 ? (
                    <ArrowUpIcon className="h-3 w-3 text-success-soft" />
                  ) : (
                    <ArrowDownIcon className="h-3 w-3 text-error-soft" />
                  )}
                  <span
                    className={
                      taxaConversaoChangeTicket >= 0
                        ? "text-success-soft"
                        : "text-error-soft"
                    }
                  >
                    {formatPercentage(taxaConversaoChangeTicket)}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                {currentMonthTicketConversion &&
                Number(currentMonthTicketConversion.conversion_rate || 0) !==
                  0 ? (
                  <EstadoDoValor
                    carregando={loadingAovConversion}
                    disponivel
                    fontes={FONTES.ga4}
                    className="text-2xl font-bold text-muted-foreground"
                  >
                    <KpiNumber
                      value={Number(
                        currentMonthTicketConversion.conversion_rate || 0,
                      )}
                      format={(n) => formatPercentValue(Number(n))}
                      className="font-bold text-foreground"
                      maxFontPx={32}
                      breathingRoomPx={24}
                      ariaLabel="Taxa de conversão"
                    />
                  </EstadoDoValor>
                ) : (
                  resumoAusente(loadingAovConversion, FONTES.ga4)
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MediaPerformancePage;
